import { type Configuration, LogLevel } from '@azure/msal-browser';

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID || '';
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID || '';

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    /**
     * ⚠ **localStorage, not sessionStorage — sessionStorage is per-TAB.**
     *
     * With a per-tab token cache, every "open in a new tab" landed on the sign-in page even though
     * the user was signed in one tab over: a fresh tab has an empty cache, so `AuthGuard` sees no
     * account and renders the unauthenticated branch. `rel="noopener"` on the record panel's
     * profile links means the new tab does not even inherit a copy of the opener's sessionStorage,
     * so it was every time, deterministically. That made the profile links shipped in #821/#827 —
     * and the deep links in notification emails — effectively unusable.
     *
     * localStorage is MSAL's documented mechanism for sharing one session across tabs.
     *
     * **The trade-off, stated plainly:** the token cache now survives a browser restart, where
     * before it died with the tab. Session length is still bounded by Entra rather than by us
     * (access tokens ~1h; refresh subject to the tenant's Conditional Access / sign-in-frequency
     * policy), and nothing else moved — `auth/impersonation.ts` keeps its own sessionStorage
     * backing precisely so "view as user" cannot outlive the tab, and the deep-link stash in
     * `auth/deepLinkStash.ts` stays per-tab too.
     */
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
    },
  },
};

export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

/** True when Entra ID is configured (client ID provided via env var). */
export const isEntraConfigured = Boolean(clientId && tenantId);
